import './common/styles/globalStyle';
import './common/i18n/i18n';

import { Global } from '@emotion/react';
import CssBaseline from '@mui/material/CssBaseline';
import StyledEngineProvider from '@mui/material/StyledEngineProvider';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import { ErrorBoundary, Provider } from '@rollbar/react'; // <-- Provider imports 'rollbar' for us
import { AuthenticationContextProvider } from '@src/authentication/AuthenticationContextProvider';
import { MeasurementContextProvider } from '@src/common/measurements/MeasurementContextProvider';
import { globalStyle } from '@src/common/styles/globalStyle';
import { muiTheme } from '@src/common/styles/muiTheme';
import { Timestamp } from '@src/common/time/Timestamp';
import { loadScript } from '@src/common/utils/loadScript';
import { QueryClientConfig } from '@src/common/utils/QueryClientConfig';
import { rollbarConfig } from '@src/RollbarConfig';
import { Router } from '@src/routing/Router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React, { useCallback, useEffect } from 'react';

const SAFE_CHARGE_SCRIPT_URL =
  'https://cdn.safecharge.com/safecharge_resources/v1/websdk/safecharge.js';

const client = new QueryClient(QueryClientConfig);
Timestamp.configureUTC();

function App() {
  const initializeSafeCharge = useCallback(async () => {
    await loadScript({
      onError: () => {},
      scriptSrc: SAFE_CHARGE_SCRIPT_URL,
    });
  }, []);

  useEffect(() => {
    void initializeSafeCharge();
  }, [initializeSafeCharge]);

  return (
    <React.StrictMode>
      <Provider config={rollbarConfig}>
        <ErrorBoundary>
          <StyledEngineProvider injectFirst>
            <Global styles={globalStyle} />
            <ThemeProvider theme={muiTheme}>
              <CssBaseline enableColorScheme />
              <QueryClientProvider client={client}>
                <MeasurementContextProvider>
                  <AuthenticationContextProvider>
                    <Router />
                  </AuthenticationContextProvider>
                </MeasurementContextProvider>
              </QueryClientProvider>
            </ThemeProvider>
          </StyledEngineProvider>
        </ErrorBoundary>
      </Provider>
    </React.StrictMode>
  );
}

export default App;
